/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useBucketCoverage — for each active bucket, how many posts (drafts +
 * scheduled + published) fall in the given month. Compared to the bucket's
 * target_monthly for a coverage ring.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import { useContentBuckets } from './useContentBuckets';

export interface BucketCoverage {
  slug: string;
  name: string;
  color: string;
  target: number | null;
  count_month: number;
  count_this_week: number;
}

function startOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfWeek(d = new Date()) {
  const day = (d.getDay() + 6) % 7; // Monday-first
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  return s;
}

export function useBucketCoverage() {
  const bucketsQ = useContentBuckets();

  const q = useQuery<Array<{ bucket_slug: string; when: string }>>({
    queryKey: ['bucket-coverage-events'],
    queryFn: async () => {
      const monthStart = startOfMonth().toISOString();
      const { data, error } = await marketing
        .from('content_draft')
        .select('bucket_slug,scheduled_at,published_at,updated_at,status')
        .neq('status', 'archived')
        .gte('updated_at', monthStart);
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        bucket_slug: d.bucket_slug,
        when: d.published_at ?? d.scheduled_at ?? d.updated_at,
      }));
    },
    staleTime: 30_000,
  });

  const rows: BucketCoverage[] = useMemo(() => {
    const buckets = bucketsQ.data ?? [];
    const events = q.data ?? [];
    const monthStart = startOfMonth();
    const weekStart = startOfWeek();
    return buckets.map((b) => {
      const forBucket = events.filter((e) => e.bucket_slug === b.slug);
      const monthCount = forBucket.filter((e) => new Date(e.when) >= monthStart).length;
      const weekCount = forBucket.filter((e) => new Date(e.when) >= weekStart).length;
      return {
        slug: b.slug, name: b.name, color: b.color_accent ?? '#888',
        target: b.target_monthly ?? null,
        count_month: monthCount,
        count_this_week: weekCount,
      };
    });
  }, [bucketsQ.data, q.data]);

  return {
    rows,
    isLoading: bucketsQ.isLoading || q.isLoading,
    isError: bucketsQ.isError || q.isError,
    error: bucketsQ.error ?? q.error,
  };
}
